import React from 'react';
import { cn } from '../lib/utils';
import { AppStoreBadge } from '../components/AppStoreBadge';
import { renderInline } from './inline';
import type { AppReviewBlock, Block, ComparisonTableBlock } from './types';

/**
 * Renders a post's blocks. Pure React: no router, no motion, no hooks that
 * need a browser, so `scripts/prerender-blog.tsx` can run it in Node and the
 * SPA page can render the identical markup on top of the snapshot.
 */

const card = 'rounded-[24px] bg-surface border-hairline shadow-card';

const Check: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Cross: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const Alert: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
);

const H2: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <h2
    id={id}
    className="scroll-mt-28 font-display font-bold text-[26px] md:text-[32px] leading-[1.15] tracking-[-0.025em] text-ink mt-14 md:mt-16 mb-5 first:mt-0"
  >
    {children}
  </h2>
);

const H3: React.FC<{ id?: string; children: React.ReactNode }> = ({ id, children }) => (
  <h3 id={id} className="scroll-mt-28 font-display font-bold text-[20px] md:text-[22px] leading-[1.25] tracking-[-0.02em] text-ink mt-10 mb-3">
    {children}
  </h3>
);

const P: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <p className={cn('text-[16px] md:text-[17px] leading-[1.7] text-ink-soft m-0 mb-5', className)}>{children}</p>
);

const List: React.FC<{ ordered?: boolean; items: string[] }> = ({ ordered, items }) => {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className={cn('m-0 mb-6 pl-6 flex flex-col gap-2', ordered ? 'list-decimal' : 'list-disc')}>
      {items.map((item, i) => (
        <li key={i} className="text-[16px] md:text-[17px] leading-[1.65] text-ink-soft pl-1 marker:text-ink-muted">
          {renderInline(item)}
        </li>
      ))}
    </Tag>
  );
};

const PlatformChips: React.FC<{ platforms: string[] }> = ({ platforms }) => (
  <span className="flex flex-wrap gap-1.5">
    {platforms.map((p) => (
      <span key={p} className="inline-flex items-center h-7 px-2.5 rounded-pill bg-surface-alt text-[13px] font-medium text-ink-soft">
        {p}
      </span>
    ))}
  </span>
);

const ScoreBadge: React.FC<{ score: number; ours?: boolean }> = ({ score, ours }) => (
  <span
    className={cn(
      'inline-flex flex-col items-center justify-center w-[64px] h-[64px] rounded-[18px] shrink-0 nums-tabular',
      ours ? 'bg-accent text-white' : 'bg-surface-alt text-ink',
    )}
    aria-label={`Score ${score.toFixed(1)} out of 10`}
  >
    <span className="font-display font-black text-[22px] leading-none tracking-[-0.03em]">{score.toFixed(1)}</span>
    <span className={cn('text-[10px] font-semibold uppercase tracking-[0.12em] mt-1', ours ? 'text-white/80' : 'text-ink-muted')}>
      / 10
    </span>
  </span>
);

const AppReview: React.FC<{ block: AppReviewBlock }> = ({ block: b }) => (
  <section
    id={b.id}
    className={cn(
      'scroll-mt-28 mt-10 mb-10 overflow-hidden',
      card,
      b.isOurs && 'border-[color:var(--accent)] ring-1 ring-[color:var(--accent)]',
    )}
  >
    {b.isOurs ? (
      <div className="flex items-center gap-2 px-6 md:px-8 py-2.5 bg-accent text-white text-[12px] font-bold uppercase tracking-[0.14em]">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
          <path d="M10 1.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L10 14.9l-5.3 2.8 1.1-5.9L1.5 7.7l5.9-.8z" />
        </svg>
        Our pick
      </div>
    ) : null}

    <div className="p-6 md:p-8">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <span
            className={cn(
              'shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-display font-black text-[17px] nums-tabular',
              b.isOurs ? 'bg-accent-soft text-accent-deep' : 'bg-ink text-white',
            )}
            aria-hidden
          >
            {b.rank}
          </span>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-[24px] md:text-[28px] leading-[1.15] tracking-[-0.025em] text-ink m-0">
              {b.rank}. {b.name}
            </h3>
            <p className="mt-1.5 text-[15px] md:text-[16px] text-ink-muted m-0">{b.tagline}</p>
          </div>
        </div>
        {b.score != null ? <ScoreBadge score={b.score} ours={b.isOurs} /> : null}
      </header>

      <div className={cn('mt-6 grid gap-6', b.image ? 'md:grid-cols-[1fr_200px]' : '')}>
        <dl className="m-0 flex flex-col gap-3.5">
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="shrink-0 sm:w-[96px] text-[12px] font-bold uppercase tracking-[0.12em] text-ink-muted pt-[3px]">Best for</dt>
            <dd className="m-0 text-[16px] leading-[1.6] text-ink-soft">{renderInline(b.bestFor)}</dd>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="shrink-0 sm:w-[96px] text-[12px] font-bold uppercase tracking-[0.12em] text-ink-muted pt-[7px]">Platforms</dt>
            <dd className="m-0"><PlatformChips platforms={b.platforms} /></dd>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="shrink-0 sm:w-[96px] text-[12px] font-bold uppercase tracking-[0.12em] text-ink-muted pt-[3px]">Pricing</dt>
            <dd className="m-0 text-[16px] leading-[1.6] text-ink-soft">{renderInline(b.pricing)}</dd>
          </div>
        </dl>
        {b.image ? (
          <figure className="m-0 hidden md:flex items-start justify-center">
            <img src={b.image.src} alt={b.image.alt} loading="lazy" className="w-[200px] h-auto object-contain drop-shadow-phone" />
          </figure>
        ) : null}
      </div>

      <div className="mt-7 grid md:grid-cols-2 gap-5">
        <div className="rounded-[18px] bg-accent-soft/60 p-5">
          <p className="m-0 mb-3 text-[12px] font-bold uppercase tracking-[0.12em] text-accent-deep">Strengths</p>
          <ul className="m-0 p-0 list-none flex flex-col gap-2.5">
            {b.strengths.map((s, i) => (
              <li key={i} className="flex gap-2.5 text-[15px] leading-[1.55] text-ink-soft">
                <Check className="w-4 h-4 mt-[3px] shrink-0 text-accent-deep" />
                <span>{renderInline(s)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-[18px] bg-surface-alt p-5">
          <p className="m-0 mb-3 text-[12px] font-bold uppercase tracking-[0.12em] text-ink-muted">Watch-outs</p>
          <ul className="m-0 p-0 list-none flex flex-col gap-2.5">
            {b.watchOuts.map((s, i) => (
              <li key={i} className="flex gap-2.5 text-[15px] leading-[1.55] text-ink-soft">
                <Alert className="w-4 h-4 mt-[3px] shrink-0 text-ink-muted" />
                <span>{renderInline(s)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-6 mb-0 text-[16px] leading-[1.65] text-ink-soft">
        <strong className="font-semibold text-ink">Verdict: </strong>
        {renderInline(b.verdict)}
      </p>

      {b.isOurs ? (
        <div className="mt-6 pt-6 border-t-hairline flex flex-col sm:flex-row sm:items-center gap-4">
          <AppStoreBadge size="md" />
          <span className="text-[14px] text-ink-muted">Free on iPhone. No card needed to start.</span>
        </div>
      ) : b.link ? (
        <p className="mt-5 mb-0 text-[14px] text-ink-muted">
          <a href={b.link.href} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-[3px]">
            {b.link.label}
          </a>
        </p>
      ) : null}
    </div>
  </section>
);

const Table: React.FC<{ block: ComparisonTableBlock }> = ({ block: t }) => (
  <figure className={cn('m-0 my-8 overflow-hidden', card)}>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[14px] md:text-[15px]">
        <thead>
          <tr className="bg-surface-alt">
            {t.columns.map((c, i) => (
              <th
                key={i}
                scope="col"
                className={cn(
                  'text-left font-semibold text-ink px-4 py-3.5 whitespace-nowrap border-b-hairline',
                  i === 0 ? 'sticky left-0 bg-surface-alt min-w-[150px]' : 'text-center min-w-[96px]',
                )}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.rows.map((row, ri) => {
            const hl = t.highlightRow === ri;
            // Solid row colours (no alpha) so the sticky first cell matches the rest of its row.
            const rowBg = hl ? 'bg-[#EEF9F3]' : ri % 2 ? 'bg-bg-alt' : 'bg-surface';
            return (
              <tr key={ri} className={rowBg}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={cn(
                      'px-4 py-3 border-b-hairline align-middle whitespace-nowrap',
                      ci === 0 ? cn('sticky left-0 font-semibold text-ink whitespace-nowrap', rowBg) : 'text-center text-ink-soft',
                      hl && 'font-medium',
                    )}
                  >
                    {typeof cell === 'boolean' ? (
                      cell ? (
                        <Check className="w-[18px] h-[18px] inline-block text-accent-deep" />
                      ) : (
                        <Cross className="w-[18px] h-[18px] inline-block text-ink-faint" />
                      )
                    ) : (
                      cell
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {t.caption ? (
      <figcaption className="px-5 py-3 text-[13px] text-ink-muted border-t-hairline">{t.caption}</figcaption>
    ) : null}
  </figure>
);

export const ArticleBody: React.FC<{ blocks: Block[] }> = ({ blocks }) => (
  <div className="blog-body">
    {blocks.map((b, i) => {
      switch (b.type) {
        case 'h2':
          return <H2 key={i} id={b.id}>{b.text}</H2>;
        case 'h3':
          return <H3 key={i} id={b.id}>{b.text}</H3>;
        case 'p':
          return <P key={i}>{renderInline(b.text)}</P>;
        case 'ul':
          return <List key={i} items={b.items} />;
        case 'ol':
          return <List key={i} ordered items={b.items} />;
        case 'callout':
          return (
            <aside
              key={i}
              className={cn(
                'my-7 rounded-[20px] p-5 md:p-6 border-l-4',
                b.tone === 'accent' ? 'bg-accent-soft/60 border-[color:var(--accent)]' : 'bg-surface-alt border-[color:var(--hairline-strong)]',
              )}
            >
              {b.title ? <p className="m-0 mb-1.5 font-display font-bold text-[17px] text-ink">{b.title}</p> : null}
              <p className="m-0 text-[16px] leading-[1.65] text-ink-soft">{renderInline(b.text)}</p>
            </aside>
          );
        case 'quickAnswers':
          return (
            <aside key={i} className={cn('my-8 p-6 md:p-7', card)}>
              <p className="m-0 mb-4 font-display font-bold text-[19px] tracking-[-0.01em] text-ink">{b.title}</p>
              <ul className="m-0 p-0 list-none flex flex-col gap-3">
                {b.items.map((it, j) => (
                  <li key={j} className="flex flex-col sm:flex-row sm:gap-3 text-[15px] md:text-[16px] leading-[1.6]">
                    <span className="shrink-0 sm:w-[190px] font-semibold text-ink">{it.label}</span>
                    <span className="text-ink-soft">{renderInline(it.text)}</span>
                  </li>
                ))}
              </ul>
            </aside>
          );
        case 'criteria':
          return (
            <ul key={i} className="m-0 mb-8 p-0 list-none flex flex-col gap-3">
              {b.items.map((c, j) => (
                <li key={j} className={cn('p-5', card, 'rounded-[18px]')}>
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <span className="font-display font-bold text-[17px] text-ink">{c.label}</span>
                    <span className="nums-tabular font-display font-bold text-[15px] text-accent-deep">{c.weight}%</span>
                  </div>
                  <div className="h-1.5 rounded-pill bg-surface-sunk overflow-hidden mb-3" aria-hidden>
                    <div className="h-full rounded-pill bg-accent" style={{ width: `${c.weight}%` }} />
                  </div>
                  <p className="m-0 text-[15px] leading-[1.6] text-ink-soft">{renderInline(c.text)}</p>
                </li>
              ))}
            </ul>
          );
        case 'image':
          return (
            <figure key={i} className="m-0 my-8">
              <img src={b.src} alt={b.alt} loading="lazy" className="w-full h-auto rounded-[20px]" />
              {b.caption ? <figcaption className="mt-2.5 text-[13px] text-ink-muted text-center">{b.caption}</figcaption> : null}
            </figure>
          );
        case 'video':
          return (
            <figure key={i} className="m-0 my-8 flex flex-col items-center">
              <video
                src={b.src}
                poster={b.poster}
                aria-label={b.label}
                controls
                muted
                playsInline
                preload="metadata"
                className="w-full max-w-[320px] h-auto rounded-[24px] bg-surface-alt shadow-card"
              />
              {b.caption ? <figcaption className="mt-2.5 text-[13px] text-ink-muted text-center">{b.caption}</figcaption> : null}
            </figure>
          );
        case 'appReview':
          return <AppReview key={i} block={b} />;
        case 'table':
          return <Table key={i} block={b} />;
        case 'faq':
          return (
            <div key={i} className="flex flex-col gap-3 my-6">
              {b.items.map((q, j) => (
                <details key={j} open className={cn('group px-6 py-5', card, 'rounded-[18px]')}>
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-display font-bold text-[17px] md:text-[18px] tracking-[-0.01em] text-ink [&::-webkit-details-marker]:hidden">
                    <span>{q.question}</span>
                    <span className="shrink-0 w-7 h-7 rounded-full bg-surface-alt text-ink-soft flex items-center justify-center transition-transform group-open:rotate-180" aria-hidden>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </summary>
                  <p className="m-0 mt-3 text-[15px] md:text-[16px] leading-[1.65] text-ink-soft">{renderInline(q.answer)}</p>
                </details>
              ))}
            </div>
          );
        case 'cta':
          return (
            <section key={i} className="my-12 rounded-[24px] bg-[#111111] text-white p-7 md:p-10">
              <p className="m-0 font-display font-bold text-[26px] md:text-[32px] leading-[1.15] tracking-[-0.025em]">{b.title}</p>
              <p className="mt-3 mb-0 text-[16px] md:text-[17px] leading-[1.6] text-white/70 max-w-xl">{renderInline(b.text)}</p>
              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                <AppStoreBadge size="md" />
                <span className="text-[14px] text-white/60">Free on iPhone</span>
              </div>
            </section>
          );
        case 'sources':
          return (
            <ul key={i} className="m-0 mb-6 p-0 list-none flex flex-col gap-2">
              {b.items.map((s, j) => (
                <li key={j} className="text-[14px] leading-[1.6] text-ink-muted">
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className="text-ink-soft underline underline-offset-[3px] decoration-hairline-strong hover:decoration-ink">
                    {s.label}
                  </a>
                  <span> · checked {s.checked}</span>
                </li>
              ))}
            </ul>
          );
        default:
          return null;
      }
    })}
  </div>
);

export default ArticleBody;
